import React, { useEffect, useRef, useState } from "react";

import Button from "@replo/design-system/components/button/Button";

interface BuildAssistantGeneratingProps {
  handleAbort: () => void;
}

const mockLoadingMessages: string[] = [
  "Applying styles",
  "Fixing layout and alignment",
  "Updating copy",
];

const BuildAssistantGenerationLoader: React.FC<
  BuildAssistantGeneratingProps
> = ({ handleAbort }) => {
  const [currentMessage, setCurrentMessage] = useState(mockLoadingMessages[0]!);
  const [dotCount, setDotCount] = useState(1);
  const usedMessagesRef = useRef<Set<string>>(
    new Set([mockLoadingMessages[0]!]),
  );
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // NOTE (Cole 2025-04-03): This works by picking a random message, and then switching it at a random interval between 3-7 seconds. It doesn't pick the same message again, if it runs out of messages it stops changing them.
  useEffect(() => {
    const scheduleNextMessage = () => {
      const interval = Math.floor(Math.random() * (7000 - 3000 + 1)) + 3000;

      timeoutRef.current = setTimeout(() => {
        const availableMessages = mockLoadingMessages.filter(
          (msg) => !usedMessagesRef.current.has(msg),
        );

        if (availableMessages.length > 0) {
          const randomIndex = Math.floor(
            Math.random() * availableMessages.length,
          );
          const newMessage = availableMessages[randomIndex];

          setCurrentMessage(newMessage!);
          usedMessagesRef.current.add(newMessage!);

          scheduleNextMessage();
        }
      }, interval);
    };

    scheduleNextMessage();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Control the ellipsis animation
  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev % 3) + 1);
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const renderDots = () => {
    return ".".repeat(dotCount);
  };

  return (
    <div className="text-foreground flex items-center gap-2 p-3">
      <img
        src="/images/templates/ai-loader.svg"
        alt="AI is thinking"
        className="h-6 w-6 animate-spin"
      />
      <span className="text-sm font-medium">
        {currentMessage}
        <span className="inline-block h-fit">{renderDots()}</span>
      </span>
      <div className="ml-auto">
        <Button variant="secondary" onClick={handleAbort}>
          <div className="flex items-center gap-1">
            Stop <span className="font-mono text-[10px]">esc</span>
          </div>
        </Button>
      </div>
    </div>
  );
};

export default BuildAssistantGenerationLoader;
